import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import {
  purchaseOrdersApi,
  QueryPurchaseOrderParams,
  CreatePurchaseOrderDto,
  ReceiveItemsDto,
} from '../services/purchase-orders.api';
import { toast } from 'sonner';
import { getErrorMessage } from '../client';

/**
 * Query hook for fetching paginated purchase orders list
 */
export function usePurchaseOrdersList(params: QueryPurchaseOrderParams = {}) {
  return useQuery({
    queryKey: queryKeys.purchaseOrders.list(params),
    queryFn: () => purchaseOrdersApi.getAll(params),
  });
}

/**
 * Query hook for fetching a single purchase order
 */
export function usePurchaseOrder(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.purchaseOrders.detail(id || ''),
    queryFn: () => purchaseOrdersApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching purchase order with shipment details
 */
export function usePurchaseOrderWithShipments(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.purchaseOrders.withShipments(id || ''),
    queryFn: () => purchaseOrdersApi.getWithShipments(id!),
    enabled: !!id,
  });
}

/**
 * Mutation hook for creating a new purchase order
 */
export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePurchaseOrderDto) => purchaseOrdersApi.create(data),
    onSuccess: () => {
      toast.success('Purchase order created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating a purchase order
 */
export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreatePurchaseOrderDto> }) =>
      purchaseOrdersApi.update(id, data),
    onSuccess: (_, variables) => {
      toast.success('Purchase order updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(variables.id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for adding an item to a purchase order
 */
export function useAddPurchaseOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      poId,
      item,
    }: {
      poId: string;
      item: { itemId: string; quantity: number; unitPrice?: number };
    }) => purchaseOrdersApi.addItem(poId, item),
    onSuccess: (_, variables) => {
      toast.success('Item added to purchase order');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(variables.poId) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for removing an item from a purchase order
 */
export function useRemovePurchaseOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ poId, itemId }: { poId: string; itemId: string }) =>
      purchaseOrdersApi.removeItem(poId, itemId),
    onSuccess: (_, variables) => {
      toast.success('Item removed from purchase order');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(variables.poId) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for submitting a purchase order for approval
 */
export function useSubmitPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrdersApi.submit(id),
    onSuccess: (_, id) => {
      toast.success('Purchase order submitted for approval');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for issuing an approved purchase order
 */
export function useIssuePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrdersApi.issue(id),
    onSuccess: (_, id) => {
      toast.success('Purchase order issued to supplier');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for receiving items from a purchase order
 */
export function useReceivePurchaseOrderItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReceiveItemsDto }) =>
      purchaseOrdersApi.receiveItems(id, data),
    onSuccess: (_, variables) => {
      toast.success('Items received successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(variables.id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseOrders.withShipments(variables.id),
      });
      // Also invalidate inventory as stock levels changed
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for cancelling a purchase order
 */
export function useCancelPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrdersApi.cancel(id),
    onSuccess: (_, id) => {
      toast.success('Purchase order cancelled');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting a purchase order
 */
export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrdersApi.delete(id),
    onSuccess: () => {
      toast.success('Purchase order deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
